import type React from "react";
import type { PropsWithChildren } from "react";

import { Text } from "@krea/shared/design-system/Text";
import { cn } from "@krea/shared/utils";

interface IProps extends PropsWithChildren {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  error?: string;
  variant?: "file" | "user";
  className?: string;
  contentList?: string[];
}

const FileSection = ({
  title,
  subtitle,
  actions,
  error,
  variant = "file",
  className,
  contentList,
  children,
}: IProps) => {
  return (
    <div
      className={cn(
        "bg-primary-150 px-4 rounded-sm",
        {
          "border border-red-500": error,
          "py-3": variant === "user",
          "py-1": variant === "file",
        },
        className,
      )}
    >
      <div className={cn("flex items-center")}>
        <div className="w-full truncate">
          {/* text-black is explicit, not inherited: a file name is content,
              not a link, and this card is dropped inside surfaces that set an
              ambient accent colour on the whole block (the selected guarantor
              row sets text-primary), which otherwise made every file name read
              as interactive. */}
          <div
            className={cn("text-sm w-full truncate font-semibold text-black")}
          >
            <span>{title}</span>
          </div>

          {subtitle && (
            <div className="text-xs font-normal text-gray-600">{subtitle}</div>
          )}

          {contentList &&
            contentList.length > 0 &&
            contentList.map((text, i) => (
              <div key={i} className={cn("text-sm w-full truncate mt-1")}>
                {text}
              </div>
            ))}

          {error && (
            <Text
              className={cn("text-sm font-normal leading-5 text-red-500 mt-1")}
            >
              {error}
            </Text>
          )}
        </div>

        {actions && (
          <div className="shrink-0 self-center pl-4">
            <div className="-mr-2 flex items-center">{actions}</div>
          </div>
        )}
      </div>

      {children}
    </div>
  );
};

export default FileSection;
